/**
 * HTML sanitization for template editor and rendered output.
 * Uses DOMPurify to strip scripts, iframes, and unsafe attributes while
 * preserving merge tokens (span.merge-token[data-key]) and safe formatting.
 *
 * Use:
 * - On editor change before saving templates
 * - On paste into Quill (via clipboard matcher)
 * - On renderTemplate() output before preview/PDF/email
 */

import DOMPurify from 'dompurify';

/** Allowed tags for rich text templates (includes table support) */
const ALLOWED_TAGS = [
  'p', 'div', 'span', 'strong', 'em', 'u', 's', 'strike',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'br', 'hr',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th',
  'img', 'a', 'blockquote', 'pre', 'code',
];

/** Allowed attributes; style is sanitized for dangerous values */
const ALLOWED_ATTR = [
  'href', 'src', 'alt', 'title', 'width', 'height',
  'class', 'data-key', 'data-token', 'target', 'rel',
  'style', 'align', 'colspan', 'rowspan',
];

/** Dangerous style property values (e.g. expression, javascript:) */
const DANGEROUS_STYLE_PATTERN = /(expression|javascript|vbscript|url\s*\(\s*['"]?\s*javascript|behavior\s*:)/i;

/**
 * Sanitize a single style value (e.g. for style="color: red; font-size: 12px").
 * Removes expression(), javascript:, vbscript:, etc.
 */
function sanitizeStyleValue(value) {
  if (typeof value !== 'string') return '';
  return value.replace(DANGEROUS_STYLE_PATTERN, '').trim();
}

/**
 * Sanitize full style attribute string.
 */
function sanitizeStyleAttribute(styleStr) {
  if (!styleStr || typeof styleStr !== 'string') return '';
  const parts = styleStr.split(';').map((part) => {
    const idx = part.indexOf(':');
    if (idx === -1) return '';
    const prop = part.slice(0, idx).trim().toLowerCase();
    const val = part.slice(idx + 1).trim();
    if (/^(expression|javascript|vbscript|behavior)/i.test(prop)) return '';
    return `${prop}: ${sanitizeStyleValue(val)}`;
  }).filter(Boolean);
  return parts.join('; ');
}

let hookAdded = false;

/**
 * Configure DOMPurify to keep merge-token spans and sanitize style safely.
 * Merge tokens: <span class="merge-token" data-key="..." data-token="...">
 */
function createSanitizeConfig() {
  if (!hookAdded && typeof DOMPurify.addHook === 'function') {
    DOMPurify.addHook('afterSanitizeAttributes', (node) => {
      if (node.hasAttribute?.('style')) {
        const style = node.getAttribute('style');
        node.setAttribute('style', sanitizeStyleAttribute(style));
      }
    });
    hookAdded = true;
  }

  return {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR],
    ADD_ATTR: ['target'],
    ADD_TAGS: [],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction'],
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
  };
}

let sanitizeConfig = null;

function getConfig() {
  if (!sanitizeConfig) {
    sanitizeConfig = createSanitizeConfig();
  }
  return sanitizeConfig;
}

/**
 * Sanitize HTML string. Preserves merge-token elements and safe formatting.
 * @param {string} dirty - Raw HTML
 * @returns {string} - Sanitized HTML
 */
export function sanitizeHtml(dirty) {
  if (dirty == null || typeof dirty !== 'string') return '';
  const config = getConfig();
  const clean = DOMPurify.sanitize(dirty, config);
  return clean;
}

/**
 * One-time hook removal (if you need to reset DOMPurify state).
 * Not required for normal use; DOMPurify keeps hooks.
 */
export function removeSanitizeHooks() {
  if (typeof DOMPurify.removeHook === 'function') {
    DOMPurify.removeHook('afterSanitizeAttributes');
  }
}

export default sanitizeHtml;
